import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash2, Mail, Clock, XCircle, CheckCircle2, Copy, KeyRound, Star, Users } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import BrandLogoUploader from "@/components/BrandLogoUploader";
import StripeConnectCard from "@/components/StripeConnectCard";
import PayoutTimelinePanel from "@/components/PayoutTimelinePanel";
import NotificationPreferencesPanel from "@/components/NotificationPreferencesPanel";
import AutoCaptureRulesPanel from "@/components/AutoCaptureRulesPanel";
import ApiKeysPanel from "@/components/ApiKeysPanel";
import WebsiteSalesAgentPanel from "@/components/WebsiteSalesAgentPanel";
import FacebookIntegrationPanel from "@/components/FacebookIntegrationPanel";
import SmsSettingsPanel from "@/components/SmsSettingsPanel";
import CommsTemplatesPanel from "@/components/CommsTemplatesPanel";
import EmailInboundStatusPanel from "@/components/EmailInboundStatusPanel";
import { BusinessTimezoneCard } from "@/components/BusinessTimezoneCard";
import { ChatGPTConnectionCard } from "@/components/ChatGPTConnectionCard";

const ROLE_META = {
  owner: { label: "Owner", color: "bg-orange-100 text-[#FF5A00]" },
  admin: { label: "Admin", color: "bg-purple-100 text-purple-700" },
  sales: { label: "Sales", color: "bg-blue-100 text-blue-700" },
  technician: { label: "Technician", color: "bg-emerald-100 text-emerald-700" },
};

export default function Settings() {
  const { business, user, refresh } = useAuth();
  const isOwner = user?.role === "owner" || user?.is_super_admin;
  const isAdmin = ["owner", "admin"].includes(user?.role) || user?.is_super_admin;

  const [form, setForm] = useState({
    business_name: "",
    service_type: "",
    target_location: "",
    service_area: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "technician", full_name: "" });
  const [inviteBusy, setInviteBusy] = useState(false);

  useEffect(() => {
    if (business) {
      setForm({
        business_name: business.business_name || "",
        service_type: business.service_type || "",
        target_location: business.target_location || "",
        service_area: business.service_area || "",
        phone: business.phone || "",
        address: business.address || "",
        contact_email: business.contact_email || "",
        notification_emails: (business.notification_emails || []).join(", "),
        notify_on_inbound_sms: business.notify_on_inbound_sms !== false,
        notify_on_inbound_email: business.notify_on_inbound_email !== false,
        notify_on_estimate_accepted: business.notify_on_estimate_accepted !== false,
      });
    }
  }, [business]);

  const loadTeam = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [m, i] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/invites"),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } catch { /* silent */ }
  }, [isAdmin]);
  useEffect(() => { loadTeam(); }, [loadTeam]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // Parse comma-separated notification emails into an array for the API.
      payload.notification_emails = (form.notification_emails || "")
        .split(",").map((s) => s.trim()).filter(Boolean);
      await api.put("/business", payload);
      toast.success("Business updated");
      await refresh();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteBusy(true);
    try {
      const { data } = await api.post("/team/invites", inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ email: "", role: "technician", full_name: "" });
      loadTeam();
      if (data?.accept_url) {
        try {
          await navigator.clipboard.writeText(data.accept_url);
          toast.info("Invite link also copied to clipboard", { duration: 6000 });
        } catch { /* clipboard may not be available */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setInviteBusy(false); }
  };

  const copyInviteLink = async (invite) => {
    if (!invite.accept_url) { toast.error("No accept URL available"); return; }
    try {
      await navigator.clipboard.writeText(invite.accept_url);
      toast.success("Invite link copied");
    } catch { toast.error("Copy failed — link: " + invite.accept_url); }
  };

  const generateResetLink = async (member) => {
    if (!window.confirm(`Generate a password reset link for ${member.email}?`)) return;
    try {
      const { data } = await api.post(`/team/members/${member.id}/reset-password-link`);
      if (data?.reset_url) {
        try {
          await navigator.clipboard.writeText(data.reset_url);
          toast.success(`Reset link copied. Expires in 1 hour.`, { duration: 8000 });
        } catch {
          window.prompt("Password reset link (copy):", data.reset_url);
        }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    try {
      await api.delete(`/team/invites/${id}`);
      toast.success("Invite revoked");
      loadTeam();
    } catch { toast.error("Failed"); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/team/members/${uid}/role`, { role });
      toast.success("Role updated");
      loadTeam();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/team/members/${uid}`);
      toast.success("Removed");
      loadTeam();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Workspace</div>
        <h1 className="font-heading text-4xl font-bold">Settings</h1>
      </div>

      <BusinessTimezoneCard />

      {isAdmin && <ChatGPTConnectionCard />}

      {/* Business profile */}
      <form onSubmit={save} className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-4 mb-8">
        <h2 className="font-heading text-xl font-bold">Business profile</h2>
        {(!form?.business_name || !form?.phone || !form?.address) && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="business-info-missing-warning">
            Fill in your business name, phone, and address — they appear on every estimate and invoice your customers receive.
          </div>
        )}
        <div>
          <Label>Business name</Label>
          <Input required value={form.business_name} onChange={(e)=>setForm({...form, business_name: e.target.value})} data-testid="settings-business-name"/>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Service type</Label>
            <Input value={form.service_type} onChange={(e)=>setForm({...form, service_type: e.target.value})} data-testid="settings-service-type"/>
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="settings-phone"/>
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={form.contact_email || ""} onChange={(e)=>setForm({...form, contact_email: e.target.value})} data-testid="settings-contact-email"/>
          </div>
          <div>
            <Label>Business address</Label>
            <Input value={form.address || ""} onChange={(e)=>setForm({...form, address: e.target.value})} data-testid="settings-address" placeholder="123 Main St, City, ST 12345"/>
          </div>
          <div>
            <Label>Target location</Label>
            <Input value={form.target_location} onChange={(e)=>setForm({...form, target_location: e.target.value})} data-testid="settings-target-location"/>
          </div>
          <div>
            <Label>Service area</Label>
            <Input value={form.service_area} onChange={(e)=>setForm({...form, service_area: e.target.value})} data-testid="settings-service-area"/>
          </div>
        </div>

        {/* Owner notifications */}
        <div className="pt-4 border-t border-zinc-100">
          <h3 className="font-heading text-md font-bold mb-1">Owner notifications</h3>
          <p className="text-xs text-zinc-500 mb-3">We'll email the addresses below whenever a customer texts, emails, or accepts an estimate. Leave empty to use your login email.</p>
          <Label>Notification email(s)</Label>
          <Input value={form.notification_emails || ""} onChange={(e)=>setForm({...form, notification_emails: e.target.value})}
                 data-testid="settings-notification-emails"
                 placeholder="owner@yourbiz.com, bookkeeper@yourbiz.com"/>
          <div className="mt-4 grid grid-cols-1 gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="settings-notify-sms"
                     checked={!!form.notify_on_inbound_sms}
                     onChange={(e)=>setForm({...form, notify_on_inbound_sms: e.target.checked})}/>
              Email me when a customer <strong>texts</strong> us
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="settings-notify-email"
                     checked={!!form.notify_on_inbound_email}
                     onChange={(e)=>setForm({...form, notify_on_inbound_email: e.target.checked})}/>
              Email me when a customer <strong>emails</strong> us
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="settings-notify-estimate"
                     checked={!!form.notify_on_estimate_accepted}
                     onChange={(e)=>setForm({...form, notify_on_estimate_accepted: e.target.checked})}/>
              Email me when a customer <strong>accepts an estimate</strong>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="settings-save-btn">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      {/* SMS Automation Templates */}
      {isAdmin && (
        <SmsAutomationSettings business={business} refresh={refresh}/>
      )}

      {/* Text Request SMS provider settings (Iter 2026-02 restore) */}
      {isAdmin && (
        <SmsSettingsPanel />
      )}

      {/* Phase 3 — Message templates (SMS + Email) */}
      {isAdmin && <CommsTemplatesPanel />}

      {/* Publish 32 fix — Email inbound diagnostics & test send */}
      {isAdmin && <EmailInboundStatusPanel />}

      {/* Business logo/photos */}
      {business?.id && (
        <div className="mb-8 space-y-6">
          <BrandLogoUploader
            businessId={business.id}
            currentUrl={business.logo_url}
            onSaved={() => refresh()}
          />
          <FileUploader resourceType="business" resourceId={business.id} title="Business photos & documents" canEdit={isAdmin}/>
        </div>
      )}

      {/* Stripe Connect — Payments (owner+) */}
      {isOwner && <StripeConnectCard />}
      {isOwner && <PayoutTimelinePanel />}
      <NotificationPreferencesPanel />
      {isAdmin && <AutoCaptureRulesPanel />}
      {isOwner && <ApiKeysPanel />}


      {/* Industry templates — Iter41 */}
      {isAdmin && <IndustryTemplatesCard />}

      {/* Post-Job Marketing Automation — Iter43 */}
      {isAdmin && <PostJobMarketingCard business={business} onSaved={refresh}/>}

      {/* Referral Program — Iter43 Phase 3 */}
      {isAdmin && <ReferralProgramCard business={business} onSaved={refresh}/>}

      {/* Website Sales Agent — Turn 1 (Preview only) */}
      {isAdmin && <WebsiteSalesAgentPanel />}

      {/* Facebook Lead Ads integration */}
      {isAdmin && <FacebookIntegrationPanel />}

      {/* Team management */}
      {isAdmin && (
        <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-8" data-testid="team-panel">
          <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold">Team members</h2>
              <p className="text-sm text-zinc-500 mt-1">Invite teammates. Roles: <b>owner</b> (full), <b>admin</b> (manage team & data), <b>sales</b> (leads & estimates), <b>technician</b> (jobs).</p>
            </div>
            <Button onClick={()=>setInviteOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="team-invite-btn">
              <UserPlus className="w-4 h-4 mr-1"/> Invite member
            </Button>
          </div>

          <div className="divide-y divide-zinc-100">
            {members.map((m) => (
              <div key={m.id} className="p-4 flex items-center justify-between" data-testid={`team-member-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-zinc-200 flex items-center justify-center text-sm font-semibold text-zinc-700">
                    {(m.full_name || m.email).slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{m.full_name || m.email}</div>
                    <div className="text-xs text-zinc-500 font-mono-accent">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isOwner && m.id !== user.id ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`team-role-select-${m.id}`}><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="technician">Technician</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`text-xs px-2.5 py-1 rounded font-mono-accent uppercase ${ROLE_META[m.role]?.color || "bg-zinc-100 text-zinc-700"}`}>{ROLE_META[m.role]?.label || m.role}</span>
                  )}
                  {m.id !== user.id && !m.is_super_admin && (
                    <button
                      onClick={()=>generateResetLink(m)}
                      className="p-1.5 rounded hover:bg-orange-50 text-[#FF5A00]"
                      title="Generate password reset link"
                      data-testid={`team-reset-link-${m.id}`}
                    >
                      <KeyRound className="w-4 h-4"/>
                    </button>
                  )}
                  {isOwner && m.id !== user.id && m.role !== "owner" && (
                    <button onClick={()=>removeMember(m.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`team-remove-${m.id}`}><Trash2 className="w-4 h-4"/></button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {invites.length > 0 && (
            <div className="border-t border-zinc-100">
              <div className="px-6 py-3 text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500 bg-zinc-50">Invites</div>
              <div className="divide-y divide-zinc-100">
                {invites.map((i) => (
                  <div key={i.id} className="p-4 flex items-center justify-between" data-testid={`invite-row-${i.id}`}>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-zinc-400"/>
                      <div>
                        <div className="text-sm font-medium">{i.email}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded font-mono-accent uppercase text-[10px] ${ROLE_META[i.role]?.color || "bg-zinc-100"}`}>{i.role}</span>
                          {i.status === "pending" ? <><Clock className="w-3 h-3"/> pending</> :
                           i.status === "accepted" ? <><CheckCircle2 className="w-3 h-3 text-emerald-500"/> accepted</> :
                           <><XCircle className="w-3 h-3 text-rose-500"/> {i.status}</>}
                        </div>
                      </div>
                    </div>
                    {i.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={()=>copyInviteLink(i)}
                          className="text-xs px-3 py-1.5 rounded border border-zinc-200 hover:border-[#FF5A00] hover:text-[#FF5A00] flex items-center gap-1"
                          data-testid={`invite-copy-${i.id}`}
                        >
                          <Copy className="w-3 h-3"/> Copy link
                        </button>
                        <button onClick={()=>revokeInvite(i.id)} className="text-xs px-3 py-1.5 rounded border border-zinc-200 hover:border-rose-300 hover:text-rose-600" data-testid={`invite-revoke-${i.id}`}>Revoke</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite a team member</DialogTitle></DialogHeader>
          <form onSubmit={sendInvite} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={inviteForm.email} onChange={(e)=>setInviteForm({...inviteForm, email: e.target.value})} data-testid="invite-form-email"/>
            </div>
            <div>
              <Label>Full name (optional)</Label>
              <Input value={inviteForm.full_name} onChange={(e)=>setInviteForm({...inviteForm, full_name: e.target.value})} data-testid="invite-form-name"/>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteForm.role} onValueChange={(v)=>setInviteForm({...inviteForm, role: v})}>
                <SelectTrigger data-testid="invite-form-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — manage team, all data</SelectItem>
                  <SelectItem value="sales">Sales — leads, estimates, campaigns</SelectItem>
                  <SelectItem value="technician">Technician — jobs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={inviteBusy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="invite-form-submit">
                {inviteBusy ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SmsAutomationSettings({ business, refresh }) {
  const [form, setForm] = useState({
    appointment_reminder_enabled: true,
    appointment_reminder_template: "",
    appointment_reminder_hours_before: 24,
    reengagement_enabled: true,
    reengagement_default_days: 335,
    reengagement_default_template: "",
    mileage_rate: 0.67,
  });
  const [busy, setBusy] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/sms/settings")
      .then(({ data }) => { if (alive) setSmsStatus(data); })
      .catch(() => { if (alive) setSmsStatus({ error: true }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (business) {
      setForm({
        appointment_reminder_enabled: business.appointment_reminder_enabled ?? true,
        appointment_reminder_template: business.appointment_reminder_template || "",
        appointment_reminder_hours_before: business.appointment_reminder_hours_before ?? 24,
        reengagement_enabled: business.reengagement_enabled ?? true,
        reengagement_default_days: business.reengagement_default_days ?? 335,
        reengagement_default_template: business.reengagement_default_template || "",
        mileage_rate: business.mileage_rate ?? 0.67,
      });
    }
  }, [business]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/business", form);
      toast.success("SMS automation settings saved");
      await refresh();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-6 mb-8" data-testid="sms-automation-panel">
      <div>
        <h2 className="font-heading text-xl font-bold">SMS automations</h2>
        <SmsDeliveryBanner s={smsStatus} />
        <p className="text-sm text-zinc-500 mt-2">Placeholders available in templates: <code className="bg-zinc-100 px-1 rounded text-xs">{"{customer_name}"}</code>, <code className="bg-zinc-100 px-1 rounded text-xs">{"{business_name}"}</code>, <code className="bg-zinc-100 px-1 rounded text-xs">{"{service}"}</code>, <code className="bg-zinc-100 px-1 rounded text-xs">{"{appointment_date}"}</code>, <code className="bg-zinc-100 px-1 rounded text-xs">{"{arrival_time}"}</code>.</p>
      </div>

      <div className="border-t border-zinc-100 pt-4">
        <label className="flex items-center gap-2 mb-3">
          <input type="checkbox" checked={form.appointment_reminder_enabled} onChange={(e)=>setForm({...form, appointment_reminder_enabled: e.target.checked})} data-testid="sms-appt-toggle"/>
          <span className="font-medium">Appointment reminders</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Reminder template</Label>
            <textarea rows={3} value={form.appointment_reminder_template} onChange={(e)=>setForm({...form, appointment_reminder_template: e.target.value})} className="w-full border border-zinc-200 rounded p-2 text-sm" data-testid="sms-appt-template-input"/>
          </div>
          <div>
            <Label>Hours before appointment</Label>
            <Input type="number" min={1} max={168} value={form.appointment_reminder_hours_before} onChange={(e)=>setForm({...form, appointment_reminder_hours_before: parseInt(e.target.value || 24)})} data-testid="sms-appt-hours-input"/>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-100 pt-4">
        <label className="flex items-center gap-2 mb-3">
          <input type="checkbox" checked={form.reengagement_enabled} onChange={(e)=>setForm({...form, reengagement_enabled: e.target.checked})} data-testid="sms-reengagement-toggle"/>
          <span className="font-medium">Customer re-engagement</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Re-engagement template (default)</Label>
            <textarea rows={3} value={form.reengagement_default_template} onChange={(e)=>setForm({...form, reengagement_default_template: e.target.value})} className="w-full border border-zinc-200 rounded p-2 text-sm" data-testid="sms-reeng-template-input"/>
          </div>
          <div>
            <Label>Default days after completion</Label>
            <Input type="number" min={1} max={1095} value={form.reengagement_default_days} onChange={(e)=>setForm({...form, reengagement_default_days: parseInt(e.target.value || 335)})} data-testid="sms-reeng-days-input"/>
            <p className="text-xs text-zinc-500 mt-1">Override per-service in the Services page.</p>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-100 pt-4">
        <Label>Mileage reimbursement rate ($/mi)</Label>
        <Input type="number" step="0.001" min={0} value={form.mileage_rate} onChange={(e)=>setForm({...form, mileage_rate: parseFloat(e.target.value || 0)})} className="max-w-[200px]" data-testid="sms-mileage-rate-input"/>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="sms-save-btn">
          {busy ? "Saving…" : "Save automation settings"}
        </Button>
      </div>
    </form>
  );
}


/**
 * Iter41 IndustryTemplatesCard — checkbox picker for which service industry
 * templates to install for this business. Idempotent on the backend, so
 * clicking Install multiple times won't duplicate categories/services and
 * won't overwrite any customization already made.
 */

function SmsDeliveryBanner({ s }) {
  // Reads from /api/sms/settings — the SAME tenant settings the send
  // path uses, so the UI always matches what will actually happen.
  if (!s) {
    return (
      <p className="text-sm text-zinc-500 mt-2" data-testid="sms-delivery-banner-loading">Checking SMS delivery status…</p>
    );
  }
  if (s.error) {
    return (
      <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600" data-testid="sms-delivery-banner-error">
        Could not load SMS delivery status.
      </div>
    );
  }
  const providerLabel = s.provider === "text_request" ? "Text Request" : (s.provider || "unknown");
  if (s.live_effective) {
    const from = s.text_request_from_number || s.text_request_from_number_masked || "configured sender";
    return (
      <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="sms-delivery-banner-live">
        <span className="font-semibold">Live</span> — texts sending via {providerLabel} from {from}. Real customer numbers will receive these messages.
      </div>
    );
  }
  if (s.provider === "text_request") {
    const missing = [];
    if (!s.api_key_configured) missing.push("API key");
    if (!s.text_request_dashboard_id) missing.push("dashboard ID");
    if (!s.text_request_from_number) missing.push("sending number");
    if (missing.length) {
      return (
        <div className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="sms-delivery-banner-notconfigured">
          <span className="font-semibold">Not configured</span> — Text Request is missing: {missing.join(", ")}. Fix in Delivery settings before arming live sending.
        </div>
      );
    }
    return (
      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="sms-delivery-banner-test">
        <span className="font-semibold">Test mode</span> — only allowlisted numbers receive texts. Arm live sending in Delivery settings to text real customers.
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="sms-delivery-banner-test">
      <span className="font-semibold">Test mode</span> — SMS provider is set to <span className="font-mono">{providerLabel}</span>. Switch to Text Request in Delivery settings to send real messages.
    </div>
  );
}

function IndustryTemplatesCard() {
  const [templates, setTemplates] = useState([]);
  const [picked, setPicked] = useState([]);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    api.get("/service-templates").then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([]));
  }, []);

  const toggle = (k) => setPicked((xs) => xs.includes(k) ? xs.filter((x) => x !== k) : [...xs, k]);

  const install = async () => {
    if (picked.length === 0) { toast.error("Pick at least one industry"); return; }
    setInstalling(true);
    try {
      const { data } = await api.post("/service-templates/install", { industries: picked });
      const created = data.summary.reduce((n, s) => n + (s.services_created?.length || 0), 0);
      const skipped = data.summary.reduce((n, s) => n + (s.services_skipped_existing?.length || 0), 0);
      toast.success(`Installed. ${created} services created${skipped ? `, ${skipped} skipped (already existed)` : ""}.`);
      setPicked([]);
    } catch {
      toast.error("Install failed");
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-8" data-testid="industry-templates-panel">
      <div className="p-6 border-b border-zinc-100">
        <h2 className="font-heading text-xl font-bold">Industry templates</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Pick the service industries your business offers. We'll install a starter catalog of categories + services with sensible default pricing. Nothing is overwritten — anything you've already customised is left alone.
        </p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map((t) => (
          <label
            key={t.key}
            className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition ${picked.includes(t.key) ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}
            data-testid={`industry-template-${t.key}`}
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={picked.includes(t.key)}
              onChange={() => toggle(t.key)}
              data-testid={`industry-template-checkbox-${t.key}`}
            />
            <div className="min-w-0">
              <div className="font-heading font-bold text-base">{t.label}</div>
              <div className="text-xs text-zinc-500 mt-1">
                {t.service_count} services · {t.seeds_pricing ? "Pricing pre-seeded (editable)" : "Prices left blank — you'll configure"}
              </div>
              {t.description && <div className="text-xs text-zinc-600 mt-2">{t.description}</div>}
            </div>
          </label>
        ))}
      </div>
      <div className="p-6 border-t border-zinc-100 flex items-center justify-between gap-3 bg-zinc-50">
        <div className="text-xs text-zinc-500">
          Selected: <strong>{picked.length}</strong>
        </div>
        <Button
          onClick={install}
          disabled={installing || picked.length === 0}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
          data-testid="industry-templates-install-btn"
        >
          {installing ? "Installing…" : "Install selected industries"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Iter43 — Post-Job Marketing Automation settings card.
 * Phase-1 fields only (photo/collage/review discount/reminder templates).
 * Referral fields land in Phase 3.
 */
function PostJobMarketingCard({ business, onSaved }) {
  const [form, setForm] = useState({
    postjob_marketing_enabled: true,
    postjob_min_photos: 2,
    postjob_max_photos: 6,
    postjob_collage_include_logo: true,
    google_review_url: "",
    review_discount_pct: 10,
    review_require_photo: true,
    review_reminder_enabled: true,
    review_reminder_delay_hours: 72,
    review_reminder_max_count: 1,
    review_request_sms_template: "",
    review_reminder_sms_template: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (business) {
      setForm({
        postjob_marketing_enabled: business.postjob_marketing_enabled ?? true,
        postjob_min_photos: business.postjob_min_photos ?? 2,
        postjob_max_photos: business.postjob_max_photos ?? 6,
        postjob_collage_include_logo: business.postjob_collage_include_logo ?? true,
        google_review_url: business.google_review_url || "",
        review_discount_pct: business.review_discount_pct ?? 10,
        review_require_photo: business.review_require_photo ?? true,
        review_reminder_enabled: business.review_reminder_enabled ?? true,
        review_reminder_delay_hours: business.review_reminder_delay_hours ?? 72,
        review_reminder_max_count: business.review_reminder_max_count ?? 1,
        review_request_sms_template: business.review_request_sms_template || "",
        review_reminder_sms_template: business.review_reminder_sms_template || "",
      });
    }
  }, [business]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/business", form);
      toast.success("Post-job marketing settings saved");
      onSaved && onSaved();
    } catch {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const templateVars = "{customer_first_name} · {business_name} · {discount_pct} · {review_link} · {collage_link}";

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="postjob-marketing-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
          <Star className="w-5 h-5 text-[#FF5A00]"/>
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold">Post-job marketing</h2>
          <p className="text-sm text-zinc-500">Turn completed jobs into Google reviews with a downloadable photo collage.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="postjob-compliance-notice">
          <strong>Google TOS compliance:</strong> the discount below rewards
          <em> photo-use permission or a verified social share of our finished-project post</em> — never
          "leaving a Google review". Google's Contributed Content Policy prohibits any incentive tied to a
          review; violating this can get reviews removed. The review request itself is neutral and
          separate (see the compliance banner on the job page).
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.postjob_marketing_enabled}
            onChange={(e) => setForm({ ...form, postjob_marketing_enabled: e.target.checked })}
            data-testid="postjob-enabled-toggle"
          />
          <span>Enable post-job marketing automation</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.postjob_collage_include_logo}
            onChange={(e) => setForm({ ...form, postjob_collage_include_logo: e.target.checked })}
            data-testid="postjob-collage-logo-toggle"
          />
          <span>Include business logo on collage</span>
        </label>

        <div>
          <Label>Minimum photos</Label>
          <Input
            type="number" min={2} max={6}
            value={form.postjob_min_photos}
            onChange={(e) => setForm({ ...form, postjob_min_photos: parseInt(e.target.value || 2) })}
            data-testid="postjob-min-photos-input"
          />
        </div>
        <div>
          <Label>Maximum photos</Label>
          <Input
            type="number" min={2} max={6}
            value={form.postjob_max_photos}
            onChange={(e) => setForm({ ...form, postjob_max_photos: parseInt(e.target.value || 6) })}
            data-testid="postjob-max-photos-input"
          />
        </div>

        <div className="md:col-span-2">
          <Label>Google Review URL</Label>
          <Input
            placeholder="https://g.page/r/…/review"
            value={form.google_review_url}
            onChange={(e) => setForm({ ...form, google_review_url: e.target.value })}
            data-testid="postjob-google-review-url"
          />
          <p className="text-xs text-zinc-500 mt-1">The direct Google review link customers should tap. Required before sending review-request SMS.</p>
        </div>

        <div>
          <Label>Photo/social promotion discount %</Label>
          <Input
            type="number" min={0} max={100} step="0.5"
            value={form.review_discount_pct}
            onChange={(e) => setForm({ ...form, review_discount_pct: parseFloat(e.target.value || 10) })}
            data-testid="postjob-discount-pct-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm mt-6">
          <input
            type="checkbox"
            checked={form.review_require_photo}
            onChange={(e) => setForm({ ...form, review_require_photo: e.target.checked })}
            data-testid="postjob-require-photo-toggle"
          />
          <span>Require photo-use permission (or verified social share) to unlock the discount</span>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.review_reminder_enabled}
            onChange={(e) => setForm({ ...form, review_reminder_enabled: e.target.checked })}
            data-testid="postjob-reminder-toggle"
          />
          <span>Enable review reminder text</span>
        </label>
        <div>
          <Label>Reminder delay (hours)</Label>
          <Input
            type="number" min={1} max={720}
            value={form.review_reminder_delay_hours}
            onChange={(e) => setForm({ ...form, review_reminder_delay_hours: parseInt(e.target.value || 72) })}
            data-testid="postjob-reminder-delay-input"
          />
        </div>
        <div>
          <Label>Max reminders per customer</Label>
          <Input
            type="number" min={0} max={3}
            value={form.review_reminder_max_count}
            onChange={(e) => setForm({ ...form, review_reminder_max_count: parseInt(e.target.value || 1) })}
            data-testid="postjob-reminder-max-input"
          />
        </div>
      </div>

      <div className="mt-4">
        <Label>Photo/social promotion SMS template</Label>
        <textarea
          rows={6}
          value={form.review_request_sms_template}
          onChange={(e) => setForm({ ...form, review_request_sms_template: e.target.value })}
          className="w-full border border-zinc-200 rounded p-2 text-sm font-mono-accent"
          data-testid="postjob-request-template"
        />
        <p className="text-[11px] text-zinc-500 mt-1">Variables: {templateVars}</p>
      </div>

      <div className="mt-4">
        <Label>Review reminder SMS template</Label>
        <textarea
          rows={3}
          value={form.review_reminder_sms_template}
          onChange={(e) => setForm({ ...form, review_reminder_sms_template: e.target.value })}
          className="w-full border border-zinc-200 rounded p-2 text-sm font-mono-accent"
          data-testid="postjob-reminder-template"
        />
      </div>

      <div className="flex justify-end mt-6">
        <Button
          onClick={save}
          disabled={busy}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="postjob-save-btn"
        >
          {busy ? "Saving…" : "Save marketing settings"}
        </Button>
      </div>
    </div>
  );
}


/** Iter43 Phase 3 — Referral Program settings. */
function ReferralProgramCard({ business, onSaved }) {
  const [form, setForm] = useState({
    referral_program_enabled: false,
    referral_discount_pct: 10,
    referral_reward_pct: 10,
    referral_reward_max_amount: 50,
    referral_sms_enabled: false,
    referral_reward_delivery_method: "manual",
    referral_sms_template: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (business) {
      setForm({
        referral_program_enabled: business.referral_program_enabled ?? false,
        referral_discount_pct: business.referral_discount_pct ?? 10,
        referral_reward_pct: business.referral_reward_pct ?? 10,
        referral_reward_max_amount: business.referral_reward_max_amount ?? 50,
        referral_sms_enabled: business.referral_sms_enabled ?? false,
        referral_reward_delivery_method: business.referral_reward_delivery_method || "manual",
        referral_sms_template: business.referral_sms_template || "",
      });
    }
  }, [business]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/business", form);
      toast.success("Referral program settings saved");
      onSaved && onSaved();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const templateVars = "{customer_first_name} · {business_name} · {referral_code} · {referral_discount_pct} · {referral_reward_pct} · {referral_reward_max_amount}";

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="referral-program-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
          <Users className="w-5 h-5 text-[#FF5A00]"/>
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold">Referral program</h2>
          <p className="text-sm text-zinc-500">Unique referral codes, referred-customer discounts, and rewards for referring customers.</p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm mb-4">
        <input
          type="checkbox"
          checked={form.referral_program_enabled}
          onChange={(e) => setForm({ ...form, referral_program_enabled: e.target.checked })}
          data-testid="referral-enabled-toggle"
        />
        <span>Enable referral program</span>
      </label>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>Referred-customer discount %</Label>
          <Input
            type="number" min={0} max={100} step="0.5"
            value={form.referral_discount_pct}
            onChange={(e) => setForm({ ...form, referral_discount_pct: parseFloat(e.target.value || 10) })}
            data-testid="referral-discount-pct"
          />
        </div>
        <div>
          <Label>Referring-customer reward %</Label>
          <Input
            type="number" min={0} max={100} step="0.5"
            value={form.referral_reward_pct}
            onChange={(e) => setForm({ ...form, referral_reward_pct: parseFloat(e.target.value || 10) })}
            data-testid="referral-reward-pct"
          />
        </div>
        <div>
          <Label>Max reward per referral ($)</Label>
          <Input
            type="number" min={0} step="1"
            value={form.referral_reward_max_amount}
            onChange={(e) => setForm({ ...form, referral_reward_max_amount: parseFloat(e.target.value || 50) })}
            data-testid="referral-reward-max"
          />
        </div>

        <div>
          <Label>Reward delivery method</Label>
          <select
            value={form.referral_reward_delivery_method}
            onChange={(e) => setForm({ ...form, referral_reward_delivery_method: e.target.value })}
            className="w-full border border-zinc-200 rounded p-2 text-sm"
            data-testid="referral-reward-delivery"
          >
            <option value="manual">Manual (owner fulfils gift card)</option>
            <option value="gift_card" disabled>Gift card provider (coming in Phase 4)</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm mt-6 md:col-span-2">
          <input
            type="checkbox"
            checked={form.referral_sms_enabled}
            onChange={(e) => setForm({ ...form, referral_sms_enabled: e.target.checked })}
            data-testid="referral-sms-toggle"
          />
          <span>Send referral thank-you SMS after invoice is marked paid</span>
        </label>
      </div>

      <div className="mt-4">
        <Label>Referral SMS template</Label>
        <textarea
          rows={5}
          value={form.referral_sms_template}
          onChange={(e) => setForm({ ...form, referral_sms_template: e.target.value })}
          className="w-full border border-zinc-200 rounded p-2 text-sm font-mono-accent"
          data-testid="referral-sms-template"
        />
        <p className="text-[11px] text-zinc-500 mt-1">Variables: {templateVars}</p>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          onClick={save}
          disabled={busy}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="referral-save-btn"
        >
          {busy ? "Saving…" : "Save referral settings"}
        </Button>
      </div>
    </div>
  );
}


